import * as React from "react";
import Modal from "../../Modal/Modal";
import Input from "../../Input-new/Input";
import Dropdown from "../../Dropdown/Dropdown";
import { downloadDocumentWord } from "../../../requests/repAgreement";
import { getData } from "../../../requests/CRUD";
import { SUMMIT_ESCROW_TRACKING } from "../../../consts/tables";
import psaModifications from "../../../psa-modifications";
import {
    escapeHtml,
    docWrap,
    moneyWords,
    moneyNumeral,
    shortDate,
} from "./RepAgreementForm";
import {
    resolveBuyer,
    resolveSellerContact,
    dayCount,
    longDate,
    addCalendarDays,
} from "./PsaForm";

/**
 * Amendment button + generator (In Neg / Under Contract tab).
 *
 * A PSA already signed is changed by a short document rather than a new
 * contract. The template is one page: a recital naming the two parties, the
 * contract they signed and the property, then a single modification clause, a
 * survival sentence and two signature blocks. No agent or broker lines - the
 * supplied template has none.
 *
 * One clause per amendment. Seven presets, the last of which is the agent's own
 * wording typed into the picker.
 *
 * Texas only. The template's title and recital name the Commercial Earnest
 * Money Contract by its exact Texas title, so an amendment generated against an
 * Oklahoma or Arkansas deal would cite a contract that does not exist.
 */

const BLANK_RULE = '____________________';

/**
 * The fallback for the Contract Effective Date, when the property has no
 * Escrow Tracking date to pull: a deal being amended was signed some weeks
 * ago, so a month back is a closer first guess than today. The field is
 * editable, so a known date is typed in.
 */
const CONTRACT_DATE_LOOKBACK = 30;

/**
 * The Contract Effective Date the deal actually has: Escrow Tracking's
 * EFFECTIVE_DATE, which the transaction coordinator sets when the signed
 * contract is receipted. Read-only here - the modal's field is a default the
 * agent may override for the generated document, and the override never
 * writes back to Escrow Tracking.
 *
 * Resolves to '' when the property has no live tracking row or the date is
 * not set yet, and the caller keeps the lookback fallback. A row marked
 * DELETED is a deal that fell out of escrow; its date is stale, so it does
 * not count.
 */
const resolveEscrowEffectiveDate = async (data = {}) => {
    if (!data.ARCIMEDES_ROW_ID) return '';
    try {
        const res = await getData({
            tableCode: SUMMIT_ESCROW_TRACKING,
            options: {
                filter: [{
                    text: String(data.ARCIMEDES_ROW_ID),
                    code: 'PROPERTY_ID',
                    type: 'exact',
                }],
            },
        });
        if (!res || !res.success || !Array.isArray(res.data) || !res.data.length) return '';
        const row = res.data[0];
        if (row.DELETED && row.DELETED !== '0') return '';
        return toUsDate(row.EFFECTIVE_DATE);
    } catch (e) {
        return '';
    }
};

const filled = (value) => String(value == null ? '' : value).trim() !== '';

export const isAmendmentAvailable = (data = {}) =>
    String(data.STATE || '').trim().toUpperCase() === 'TX';

const presetFor = (value) => psaModifications.find((row) => row.VALUE === value) || null;

/**
 * The amendment cites dates long-form the way its recital does: "July 22,
 * 2024". ISO is what the native picker holds; the US shape is accepted too, so
 * a value typed or pasted in either way parses.
 */
const parseFormDate = (value) => {
    const text = String(value == null ? '' : value).trim();

    const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

    let year;
    let month;
    let day;
    if (iso) {
        [, year, month, day] = iso.map(Number);
    } else if (us) {
        [, month, day, year] = us.map(Number);
    } else {
        return null;
    }

    if (!month || !day || !year || month < 1 || month > 12 || day < 1 || day > 31) return null;
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
};

const pad = (n) => String(n).padStart(2, '0');

/**
 * The form holds its dates the way the office writes them - MM/DD/YYYY - and
 * the fields are plain text rather than native date pickers: a native
 * <input type="date"> renders in the viewer's locale, so on a day-first
 * machine the same value reads 19/06 where the agent expects 06/19.
 * parseFormDate accepts ISO too, so a value pasted from elsewhere in the app
 * still parses, and valueType="date" is safe here because formatDate() keeps
 * digits and slashes.
 */
const usOf = (date) => `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;

export const toUsDate = (value) => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : usOf(value);
    const date = parseFormDate(value);
    return date ? usOf(date) : '';
};

export const formDateText = (value) => {
    const date = parseFormDate(value);
    return date ? longDate(date) : '';
};

// "7" -> "seven (7) days". Set as it is read, mid-sentence: the PSA's own day
// counts are lower case too, and only Oklahoma's are title case.
export const dayText = (value) => {
    const n = Number(String(value == null ? '' : value).replace(/[^\d]/g, ''));
    if (!Number.isFinite(n) || n <= 0) return BLANK_RULE;
    return `${dayCount(n)} days`;
};

// Currency reads exactly as it does in the PSA itself - no cents.
export const priceText = (value) =>
    (filled(value) ? `${moneyWords(value)} (${moneyNumeral(value)})` : BLANK_RULE);

/** The one clause the amendment carries, as its own paragraph. */
export const clauseText = (form = {}) => {
    const preset = presetFor(form.modification);
    if (!preset) {
        // Free-typed into the picker: the agent's own wording, verbatim.
        return String(form.modification || '').trim();
    }

    const untilDate = formDateText(form.clauseDate) || BLANK_RULE;
    const days = dayText(form.clauseDays);

    switch (preset.VALUE) {
        case 'FEASIBILITY_UNTIL':
            return `The Feasibility Period and Right to Terminate is hereby extended until ${untilDate}.`;
        case 'FEASIBILITY_DAYS':
            return `The Feasibility Period and Right to Terminate is hereby extended ${days}.`;
        case 'CLOSING_UNTIL':
            return `The Closing Date is hereby extended until ${untilDate}.`;
        case 'CLOSING_DAYS':
            return `The Closing Date is hereby extended ${days}.`;
        case 'PRICE_REDUCED':
            return `The Purchase Price is hereby reduced to ${priceText(form.clausePrice)}.`;
        case 'WAIVE_FEASIBILITY_CREDIT':
            return `The Buyer hereby waives the Feasibility Period and Right to Terminate. Additionally, the Seller hereby agrees to credit the Buyer ${priceText(form.clausePrice)} at the Closing.`;
        case 'FREE_TEXT':
            return String(form.clauseText || '').trim();
        default:
            return '';
    }
};

/**
 * What the document is called, and so what it saves as from both the print
 * dialog and the Word download:
 *
 *   Amendment to PSA - Clutch Auto - Houston, TX (Vito 123 LLC) 8.26.26
 */
export const documentTitleFor = (data = {}, form = {}) => {
    const head = ['Amendment to PSA', data.NAME, [data.CITY, data.STATE].filter(Boolean).join(', ')]
        .filter(Boolean)
        .join(' - ');
    const buyer = String(form.buyerEntity || '').trim();
    return [head, buyer ? `(${buyer})` : '', shortDate()].filter(Boolean).join(' ');
};

export const buildAmendmentHtml = (ctx) => {
    const { sellerEntity, buyerEntity, contractDate, propertyName, propertyLocation, clause, documentTitle } = ctx;

    const body = `
    <h1>AMENDMENT TO COMMERCIAL EARNEST MONEY CONTRACT</h1>

    <p>This document is an amendment ("Amendment") to the Commercial Earnest Money Contract (Real Estate Purchase Agreement) ("Agreement") between ${escapeHtml(sellerEntity || BLANK_RULE)} ("Seller") and ${escapeHtml(buyerEntity ? `${buyerEntity} and/or Assigns` : BLANK_RULE)} ("Buyer") executed by Seller on ${escapeHtml(contractDate || BLANK_RULE)} for that certain real property referred to as ${escapeHtml(propertyName || BLANK_RULE)} generally located at ${escapeHtml(propertyLocation || BLANK_RULE)}</p>

    <p>Seller and Buyer agree to modify the Agreement as follows:</p>

    <p>${escapeHtml(clause || BLANK_RULE)}</p>

    <p style="margin-top:24px;">Except as specifically modified hereby, the Agreement shall continue unchanged and in full force and effect.</p>

    <div class="sig-group">
        <div class="sig-block">
            <div class="sig-heading">BUYER: ${escapeHtml(buyerEntity || BLANK_RULE)}</div>
            <div class="tab-row"><span>BY:&nbsp;&nbsp;__________________________________</span><span class="tab-right">DATE:&nbsp;&nbsp;______________</span></div>
        </div>
        <div class="sig-block">
            <div class="sig-heading">SELLER: ${escapeHtml(sellerEntity || BLANK_RULE)}</div>
            <div class="tab-row"><span>BY:&nbsp;&nbsp;__________________________________</span><span class="tab-right">DATE:&nbsp;&nbsp;______________</span></div>
        </div>
    </div>

    <!-- 28px, plus two blank lines at the body's own line height. -->
    <p style="margin-top:62px;"><strong>NO REPRESENTATION IS MADE BY AGENT AS TO THE LEGAL EFFECT OF THIS AMENDMENT.&nbsp; A REAL ESTATE BROKER IS QUALIFIED TO GIVE ADVICE ON REAL ESTATE MATTERS.&nbsp; IF YOU DESIRE LEGAL, FINANCIAL OR TAX ADVICE, CONSULT YOUR ATTORNEY, ACCOUNTANT OR TAX ADVISOR.</strong></p>
    `;

    // The receipt row's right tab stop travels with the PSA's own stylesheet.
    return docWrap(documentTitle, body, `
        .tab-row { display: flex; justify-content: space-between; gap: 24px; margin: 26px 0 10px; }
    `);
};

const emptyForm = (data = {}) => ({
    sellerEntity: '',
    buyerEntity: '',
    // Empty until the Escrow Tracking lookup answers - seeding a guess first
    // would flash a wrong date and then swap it under the agent's cursor.
    contractDate: '',
    modification: '',
    clauseDate: '',
    clauseDays: '',
    clausePrice: '',
    clauseText: '',
});

// Without these the amendment does not say what it is changing, or to whom.
const REQUIRED = [
    ['sellerEntity', 'Seller'],
    ['buyerEntity', 'Buyer'],
    ['contractDate', 'Contract Effective Date'],
];

const PsaAmendmentForm = ({ data = {}, deleted = false }) => {
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState(() => emptyForm(data));
    const [error, setError] = React.useState('');

    const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const reset = () => {
        setForm(emptyForm(data));
        setError('');
        setOpen(false);
    };

    const openForm = () => {
        const seeded = emptyForm(data);
        setForm(seeded);
        setError('');
        setOpen(true);

        resolveSellerContact(data).then((seller) => {
            if (!seller || !seller.entity) return;
            setForm((prev) => ({ ...prev, sellerEntity: prev.sellerEntity || seller.entity }));
        });
        resolveBuyer(data).then((buyer) => {
            if (!buyer || !buyer.company) return;
            setForm((prev) => ({ ...prev, buyerEntity: prev.buyerEntity || buyer.company }));
        });
        // The field stays empty until Escrow Tracking answers; only a deal
        // with no receipted date falls back to the lookback guess. Never
        // overwrites a date the agent has already typed in the meantime.
        resolveEscrowEffectiveDate(data).then((effectiveDate) => {
            const value = effectiveDate
                || toUsDate(addCalendarDays(new Date(), -CONTRACT_DATE_LOOKBACK));
            setForm((prev) => (
                prev.contractDate ? prev : { ...prev, contractDate: value }
            ));
        });
    };

    // Choosing a preset clears the inputs the previous one used, and seeds the
    // day count the preset defaults to.
    const chooseModification = (value) => {
        const preset = presetFor(value);
        setForm((prev) => ({
            ...prev,
            modification: value,
            clauseDate: '',
            clausePrice: '',
            clauseText: '',
            clauseDays: (preset && preset.default) || '',
        }));
    };

    const buildDocument = () => {
        const documentTitle = documentTitleFor(data, form);
        return {
            html: buildAmendmentHtml({
                documentTitle,
                sellerEntity: form.sellerEntity,
                buyerEntity: form.buyerEntity,
                contractDate: formDateText(form.contractDate) || form.contractDate,
                propertyName: data.NAME || '',
                propertyLocation: [data.ADDRESS, [data.CITY, data.STATE].filter(Boolean).join(', ')]
                    .filter(Boolean).join(', ') + '.',
                clause: clauseText(form),
            }),
            title: documentTitle,
        };
    };

    const validate = () => {
        const missing = REQUIRED.filter(([key]) => !filled(form[key])).map(([, label]) => label);
        if (!filled(clauseText(form))) missing.push('Modification');
        if (!missing.length) return true;
        setError(`${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required.`);
        return false;
    };

    const generate = () => {
        if (!validate()) return;
        const { html, title } = buildDocument();

        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';

        const previousTitle = document.title;
        const cleanup = () => {
            document.title = previousTitle;
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        };

        iframe.onload = () => {
            const cw = iframe.contentWindow;
            cw.focus();
            cw.onafterprint = cleanup;
            cw.print();
            setTimeout(cleanup, 60000);
        };

        document.title = title;
        iframe.srcdoc = html;
        document.body.appendChild(iframe);

        reset();
    };

    const downloadWord = async () => {
        if (!validate()) return;
        const { html, title } = buildDocument();
        await downloadDocumentWord(html, title);
        reset();
    };

    if (!isAmendmentAvailable(data)) return null;

    const preset = presetFor(form.modification);
    const clausePreview = clauseText(form);

    return (
        <React.Fragment>
            <div className="In-negotiation_column_item" style={{ minHeight: 0, margin: 0, marginBottom: '16px' }}>
                <button
                    type="button"
                    className="button-new"
                    style={{ border: '1px solid rgb(144, 185, 237)', color: 'rgb(144, 185, 237)', background: 'transparent', width: 'auto', padding: '0px 16px', margin: 0 }}
                    disabled={deleted}
                    onClick={(e) => { e.preventDefault(); openForm(); }}
                >
                    AMENDMENT TO PSA
                </button>
            </div>

            <Modal className="loi-main" show={open} onClose={reset}>
                <div className="loi-modal">
                    <span className="green-title"> Amendment to PSA </span>
                    <div className="close-button" onClick={reset} />
                </div>
                <div
                    className="loi-modal-content"
                    style={{ display: 'grid', gap: '4px 40px', gridTemplateColumns: '1fr 1fr 1fr' }}
                >
                    <Input
                        label="Seller Entity"
                        value={form.sellerEntity}
                        onChange={(event) => update('sellerEntity', event.target.value)}
                    />
                    <Input
                        label="Buyer Entity"
                        value={form.buyerEntity}
                        onChange={(event) => update('buyerEntity', event.target.value)}
                    />
                    <Input
                        label="Contract Effective Date"
                        valueType="date"
                        placeholder="MM/DD/YYYY"
                        value={form.contractDate}
                        onChange={(event) => update('contractDate', event.target.value)}
                    />
                    <div style={{ gridColumn: '1 / -1' }}>
                        <Dropdown
                            label="Modification"
                            className="psa-modification"
                            optionsType="psa-modifications"
                            customValues
                            handleParentChange={(next) => chooseModification(next.modification)}
                            valueKey="modification"
                            value={form.modification}
                            optionLabelKey="LABEL"
                            optionKey="VALUE"
                            disablePortal
                        />
                    </div>

                    {preset && preset.input === 'date' && (
                        <Input
                            label="Extended until"
                            valueType="date"
                            placeholder="MM/DD/YYYY"
                            value={form.clauseDate}
                            onChange={(event) => update('clauseDate', event.target.value)}
                        />
                    )}
                    {preset && preset.input === 'days' && (
                        <React.Fragment>
                            <Input
                                label="Extended (days)"
                                value={form.clauseDays}
                                onChange={(event) => update('clauseDays', event.target.value)}
                            />
                        </React.Fragment>
                    )}
                    {preset && preset.input === 'currency' && (
                        <Input
                            label="Amount"
                            value={form.clausePrice}
                            onChange={(event) => update('clausePrice', event.target.value)}
                        />
                    )}
                    {preset && preset.input === 'text' && (
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Input
                                label="Clause"
                                value={form.clauseText}
                                onChange={(event) => update('clauseText', event.target.value)}
                            />
                        </div>
                    )}
                </div>

                {clausePreview && (
                    <div style={{ marginTop: '12px', padding: '10px 12px', background: '#f4f6fa', borderRadius: '4px', fontStyle: 'italic' }}>
                        {clausePreview}
                    </div>
                )}
                {error && <div style={{ color: '#d9534f', marginTop: '12px' }}>{error}</div>}

                <div className="modal-buttons" style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', marginTop: '16px' }}>
                    <button type="button" className="button-new positive" onClick={generate}>Generate</button>
                    <button type="button" className="button-new negative" onClick={downloadWord}>Word</button>
                    <button type="button" className="button-new negative" onClick={reset}>Cancel</button>
                </div>
            </Modal>
        </React.Fragment>
    );
};

export default PsaAmendmentForm;
